import { BACKEND_DATE_FORMATS, UTCMoment } from 'src/utils/UTCMoment';
import {
  ChartEnergyBillsAndNetTraded,
  EChartEnergyBillsAndNetTradedType,
} from 'src/components/_charts/ChartEnergyBillsAndNetTraded';
import { ChartSelf, EChartSelfType } from 'src/components/_charts/ChartSelf';
import React, { useMemo } from 'react';
import {
  selectAssetByUuid,
  selectAssetValuesForUuid,
  selectAssetsUnderUuid,
  selectAssetsValues,
  selectCommunityAsset,
} from 'src/redux/configuration/configuration.selectors';

import { AssetCount } from 'src/components/AssetCount';
import { ChartDayProfile } from 'src/components/_charts/ChartDayProfile';
import { ChartDeviceProfile } from 'src/components/_charts/ChartDeviceProfile';
import { ChartEnergyTradeProfile } from 'src/components/_charts/ChartEnergyTradeProfile';
import { ChartMountain } from 'src/components/_charts/ChartMountain';
import { ChartPrices } from 'src/components/_charts/ChartPrices';
import { ChartSavings } from 'src/components/ChartSavings';
import { TSimulationResultsPresentationProps } from './SimulationResultsPresentation.types';
import { TradesBidsAndOffers } from 'src/components/TradesBidsAndOffers';
import classnames from 'classnames';
import s from './SimulationResultsPresentation.module.scss';
import { selectSidebarExpanded } from 'src/redux/application/application.selectors';
import useAllTimestamps from 'src/hooks/useAllTimestamps';
import { useParsedEnergyTradeProfile } from 'src/components/_charts/ChartEnergyTradeProfile/useParsedEnergyTradeProfile';
import { useParsedPriceEnergyDay } from 'src/components/_charts/ChartPrices/useParsedPriceEnergyDay';
import { useSelector } from 'react-redux';

export const SimulationResultsPresentation: React.FC<TSimulationResultsPresentationProps> = ({
  settingsData,
  simulationResults,
  jobId,
}) => {
  const {
    assetsInfo,
    assetUuid,
    cumulativeGridTrades,
    currentMarket,
    energyTradeProfileAreaThroughput,
    kpi,
    marketSummary,
    priceEnergyAreaBalance,
    priceEnergyDay,
    deviceStatistics,
    latestSavingsKpi,
    savingsKpiProfile,
  } = simulationResults || {};

  const assetsValues = useSelector(selectAssetsValues);
  const sidebarExpanded = useSelector(selectSidebarExpanded);
  const asset = useSelector(selectAssetByUuid(assetUuid || ''));
  const assetValues = useSelector(selectAssetValuesForUuid(assetUuid || ''));
  const childrenAssets = useSelector(selectAssetsUnderUuid(assetUuid || ''));
  const communityAsset = useSelector(selectCommunityAsset);
  const assetName = assetsValues[assetUuid || '']?.name || '';
  const assetType = asset?.type || 'Area';
  const isCommunity = communityAsset?.uuid === assetUuid;

  const parsedEnergyTradeProfile = useParsedEnergyTradeProfile({
    data: energyTradeProfileAreaThroughput,
    assetName,
    assetUuid,
  });

  const parsedPriceEnergyDay = useParsedPriceEnergyDay({
    data: priceEnergyDay,
  });

  const startUnix = useMemo(() => {
    return UTCMoment.fromBackend(settingsData.startDate, BACKEND_DATE_FORMATS.SETTINGS_DATA).unix();
  }, [settingsData.startDate]);

  const allTimestamps = useAllTimestamps({
    startTimestamp: startUnix * 1000,
    endTimestamp: currentMarket && currentMarket * 1000,
    interval: 15,
  });

  return (
    <>
      {/* PH-761-Home results: remove the 5 summary/asset blocks */}
      {/* Temporarily Disable Mountain chart until it's updated with pixel sorting&transparency */}
      {/* <ChartMountain /> */}

      {/* Disabled until the backend is fixed */}
      {/* {assetType === 'Area' && !isCommunity && (
        <ChartSavings
          latestSavingsKpi={latestSavingsKpi}
          savingsKpiProfile={savingsKpiProfile}
          assetValues={assetValues}
          childrenCount={childrenAssets.length}
          currency={settingsData.currency}
        />
      )} */}
      {assetType === 'Area' && isCommunity && <AssetCount assetsInfo={assetsInfo} />}
      <ChartEnergyBillsAndNetTraded
        priceEnergyAreaBalance={priceEnergyAreaBalance}
        type={EChartEnergyBillsAndNetTradedType.Compact}
      />
      {assetType !== 'Area' && (
        <ChartDeviceProfile
          settingsData={settingsData}
          startUnix={startUnix}
          currentMarket={currentMarket}
          deviceStatics={deviceStatistics}
          deviceType={assetType}
          forTimestamps={allTimestamps}
        />
      )}
      {assetType === 'Area' && (
        <div
          className={classnames(s.selfChartsWrapper, {
            [s.rowDisplay]: sidebarExpanded,
          })}>
          <ChartSelf kpi={kpi} type={EChartSelfType.Sufficiency} />
          <ChartSelf kpi={kpi} type={EChartSelfType.Consumption} />
        </div>
      )}
      {assetType === 'Area' && (
        <ChartEnergyTradeProfile
          {...parsedEnergyTradeProfile}
          assetUuid={assetUuid}
          startUnix={startUnix}
          currentMarket={currentMarket}
          hasData={Boolean(energyTradeProfileAreaThroughput)}
        />
      )}
      {assetType === 'Area' && (
        <ChartDayProfile
          marketSummary={marketSummary}
          netEnergyData={parsedEnergyTradeProfile.netEnergyData}
          gridFeeData={parsedPriceEnergyDay?.gridFee}
          startUnix={startUnix}
          currentMarket={currentMarket}
        />
      )}
      {assetType === 'Area' && (
        <ChartPrices
          parsedPriceEnergyDay={parsedPriceEnergyDay}
          startUnix={startUnix}
          currentMarket={currentMarket}
        />
      )}
      {assetType === 'Area' && (
        <TradesBidsAndOffers
          cumulativeGridTrades={cumulativeGridTrades}
          assetUuid={assetUuid}
          jobId={jobId}
          currentMarket={currentMarket}
        />
      )}
    </>
  );
};
